import { resolveRequirementStatus } from './requirementStatus';

/**
 * Partnership management access:
 * Team Leader (management_id 1 / is_team_leader) or Partnership bureau (bureau_id 1) only.
 * Admins are not elevated unless they also match one of the above roles.
 */

export function isTeamLeader(user) {
  if (!user) return false;
  return Boolean(user.is_team_leader) || Number(user.management_id) === 1;
}

export function isPartnershipBureau(user) {
  if (!user) return false;
  return Number(user.bureau_id) === 1;
}

export function canAccessPartnershipManagement(user) {
  return isTeamLeader(user) || isPartnershipBureau(user);
}

export function isRequirementOwner(user, requirement) {
  if (!user || !requirement) return false;
  const ownerId = requirement.created_by ?? requirement.user_id ?? requirement.user?.id;
  return Number(ownerId) === Number(user.id);
}

export function canOwnerManageNotStartedRequirement(user, requirement) {
  if (!isRequirementOwner(user, requirement)) return false;
  return resolveRequirementStatus(requirement) === 'not_started';
}

export function canManageRequirement(user, requirement) {
  if (!user || !requirement) return false;
  if (canAccessPartnershipManagement(user)) return true;
  return isRequirementOwner(user, requirement);
}

/** Partnership staff always; owners while status is not_started. */
export function canEditRequirement(user, requirement = null) {
  if (!user) return false;
  if (canAccessPartnershipManagement(user)) return true;
  if (!requirement) return false;
  return canOwnerManageNotStartedRequirement(user, requirement);
}

/** Kanban drag / status changes — partnership staff only. */
export function canUpdateRequirementStatus(user) {
  return canAccessPartnershipManagement(user);
}

/** Partnership staff may archive an active requirement. */
export function canCancelRequirement(user, requirement) {
  if (!canAccessPartnershipManagement(user) || !requirement) return false;
  return !requirement?.archived_at;
}

/** Company is linked to partner records. Kept for warnings and reporting. */
export function companyHasDeleteBlockers(company) {
  if (!company) return false;
  const partners = company.partners_count ?? company.partnersCount ?? 0;
  if (Number(partners) > 0) return true;
  return Array.isArray(company?.partners) && company.partners.length > 0;
}

/** Human-readable note for companies linked to partner records. */
export function getCompanyDeleteBlockerMessage(company) {
  if (!company) {
    return 'This company is linked to partner records. Archiving will preserve those records.';
  }

  const count = Number(company.partners_count ?? company.partnersCount ?? company.partners?.length ?? 0);
  if (count === 1) {
    return 'This company is linked to 1 partner record. Archiving will preserve that record.';
  }
  if (count > 1) {
    return `This company is linked to ${count} partner records. Archiving will preserve those records.`;
  }

  return 'This company is linked to partner records. Archiving will preserve those records.';
}

/** User may archive company records independently of workflow status. */
export function canDeleteCompany(user, company) {
  if (!canAccessPartnershipManagement(user) || !company) return false;
  return !company.archived_at;
}
